import { inject } from 'inversify';

import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { CrewMemberStore } from '@/stores/CrewMemberStore';
import { formatName } from '@/utils/formatName';
import { orderByField } from '@/utils/orderBy';
import type { ICrewMember } from '~/api/ICrewMember';
import { ICrewMembersRespMsg } from '~/api/messages/ICrewMembersRespMsg';
import type { IDispatchMessage } from '~/api/messages/IDispatchMessage';

@SignalrListener
@service(CrewMemberManager)
export class CrewMemberManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(CrewMemberStore) protected crewMemberStore: CrewMemberStore;

  get all(): ICrewMember[] {
    const crewMembers = this.crewMemberStore.all();
    const sorted = orderByField(
      crewMembers.map((cm) => ({ cm, value: formatName(cm) })),
      'value',
    );
    return sorted.map(({ cm }) => cm);
  }

  find(id: string | number) {
    return this.crewMemberStore.find(id);
  }

  update(crewMember: ICrewMember) {
    this.crewMemberStore.update(crewMember);
  }

  // Handle list response (plural)
  @SignalrMessage('CrewMembersRespMsg')
  async onCrewMembersRespMsg(crewMembersMsg: ICrewMembersRespMsg & IDispatchMessage) {
    this.crewMemberStore.set([]);
    this.crewMemberStore.batchUpdate(crewMembersMsg.crewMembers);
  }
}
